import { catchError, map, switchMap, tap } from "rxjs/operators";
import { Observable, of, throwError } from "rxjs";
import { Injectable } from "@angular/core";
import {
  CompanyActivityResolution,
  CompanyActivityFilter,
  resolveCompanyActivityIds,
} from "../utils/company-activity";
import {
  CompaniesFilter,
  CompaniesList,
  CompanieSortKeys,
  CompanyProducts,
  CreateCompanyInput,
  SortType,
  UpdateCompanyInput,
} from "../../models/phoenix/globalTypes";
import { Apollo } from "apollo-angular";
import {
  CreateCompanyMutation,
  QueryCompanies,
  QueryCompaniesOverview,
  QueryCompaniesSelect,
  UpdateCompanyMutation,
} from "../queries/phoenix/companies";
import { QueryCompanyEnabledProducts } from "../queries/phoenix/company-enabled-products";
import { Companies } from "src/models/phoenix/Companies";
import { CreateCompany } from "src/models/phoenix/CreateCompany";
import { UpdateCompany } from "src/models/phoenix/UpdateCompany";
import { ToastrService } from "ngx-toastr";
import {
  CompaniesOverview,
  CompaniesOverview_companies_objects,
} from "../../models/phoenix/CompaniesOverview";

export const emptyFilter: CompaniesFilter = {
  ids: [],
  names: [],
  accountIds: [],
  countryCodes: [],
};

export const emptyCompaniesList: CompaniesList = {
  limit: 2000,
  order: {
    key: CompanieSortKeys.name,
    type: SortType.asc,
  },
};

@Injectable({
  providedIn: "root",
})
export class CompaniesService {
  constructor(
    private apollo: Apollo,
    private toastr: ToastrService,
  ) {}

  companies(
    list: CompaniesList = emptyCompaniesList,
    filter: CompaniesFilter = emptyFilter,
  ) {
    return this.apollo
      .use("phoenix")
      .query<CompaniesOverview>({
        query: QueryCompaniesOverview,
        variables: {
          list,
          filter,
          registeredCountriesFilter2: {
            registeredAt: null,
          },
        },
        fetchPolicy: "cache-first",
      })
      .pipe(map((response) => response.data.companies.objects));
  }

  companiesSelect(
    list: CompaniesList = emptyCompaniesList,
    filter: CompaniesFilter = emptyFilter,
  ) {
    return this.apollo
      .use("phoenix")
      .query<CompaniesOverview>({
        query: QueryCompaniesSelect,
        variables: {
          list,
          filter,
        },
        fetchPolicy: "cache-first",
      })
      .pipe(map((response) => response.data.companies.objects));
  }

  private allCompaniesForActivity(
    skip = 0,
    accumulated: CompaniesOverview_companies_objects[] = [],
  ): Observable<CompaniesOverview_companies_objects[]> {
    const list = { ...emptyCompaniesList, skip };

    return this.apollo
      .use("phoenix")
      .query<CompaniesOverview>({
        query: QueryCompaniesSelect,
        variables: { list, filter: emptyFilter },
        fetchPolicy: "cache-first",
      })
      .pipe(
        switchMap((response) => {
          const result = response.data.companies;
          const page = (result?.objects || []).filter(
            (company): company is CompaniesOverview_companies_objects =>
              company != null,
          );
          const companies = [...accumulated, ...page];

          if (
            !result ||
            result.objects.length === 0 ||
            companies.length >= result.metaData.count
          ) {
            return of(companies);
          }

          return this.allCompaniesForActivity(
            skip + result.objects.length,
            companies,
          );
        }),
      );
  }

  companyIdsByActivity(activity: CompanyActivityFilter): Observable<string[]> {
    return this.resolveGroupIdsByActivity(activity, []).pipe(
      map((resolution) => resolution.groupIds),
    );
  }

  /**
   * Resolves the effective groupIds for a company-activity scope. Explicitly
   * selected companies are intersected with the selected activity state.
   */
  resolveGroupIdsByActivity(
    activity: CompanyActivityFilter | null | undefined,
    selectedGroupIds: string[] | null | undefined,
  ): Observable<CompanyActivityResolution> {
    if (!activity || activity === CompanyActivityFilter.ALL) {
      return of(resolveCompanyActivityIds(activity, selectedGroupIds, []));
    }

    return this.allCompaniesForActivity().pipe(
      map((companies) =>
        resolveCompanyActivityIds(activity, selectedGroupIds, companies),
      ),
      catchError((error) => {
        this.toastr.error(
          "Unable to load company activity. Please try again.",
          "Company Activity Filter",
        );
        return throwError(() => error);
      }),
    );
  }

  getNameById(id: string) {
    return this.apollo
      .use("phoenix")
      .query<CompaniesOverview>({
        query: QueryCompaniesOverview,
        variables: {
          list: emptyCompaniesList,
          filter: {
            ids: [id],
            names: [],
            accountIds: [],
            countryCodes: [],
          },
        },
        fetchPolicy: "cache-first",
      })
      .pipe(map((response) => response.data.companies.objects[0].name));
  }

  byId(companyId: string) {
    const list = {
      skip: 0,
      limit: 1,
    };

    const filter = {
      ...emptyFilter,
      ids: [companyId],
    };

    return this.apollo
      .use("phoenix")
      .query<Companies>({
        query: QueryCompanies,
        variables: {
          list,
          filter,
          registeredCountriesFilter2: {
            registeredAt: null,
          },
        },
      })
      .pipe(map((response) => response.data.companies.objects));
  }

  create(input: CreateCompanyInput) {
    return this.apollo
      .use("phoenix")
      .mutate<CreateCompany>({
        mutation: CreateCompanyMutation,
        variables: {
          input,
          filter: {
            registeredAt: null,
          },
        },
        // refetchQueries: [
        //   {
        //     query: QueryAccounts,
        //     variables: { list: emptyAccountsList, filter: emptyAccountsFilter },
        //   },
        // ],
      })
      .pipe(
        tap((response) => this.toastr.success("Created Successfuly!")),
        map((response) => response.data.createCompany),
      );
  }

  update(input: UpdateCompanyInput) {
    return this.apollo
      .use("phoenix")
      .mutate<UpdateCompany>({
        mutation: UpdateCompanyMutation,
        variables: {
          input,
          filter: {
            registeredAt: null,
          },
        },
        // refetchQueries: [
        //   {
        //     query: QueryAccounts,
        //     variables: { list: emptyAccountsList, filter: emptyAccountsFilter },
        //   },
        // ],
      })
      .pipe(
        tap((response) => this.toastr.success("Updated Successfuly!")),
        map((response) => response.data.updateCompany),
      );
  }

  /**
   * Fetches companies with enabled products.
   *
   * @param enabledProduct - The product to filter by (e.g., "apai")
   * @param companyIds - Array of company IDs to filter by. Empty array for admins to get all companies.
   * @param list - Optional pagination and sorting parameters
   * @returns Observable of companies with enabled products
   */
  companiesWithEnabledProducts(
    enabledProduct: CompanyProducts,
    companyIds: string[],
    list: CompaniesList = emptyCompaniesList,
  ) {
    const filter: CompaniesFilter = {
      enabledProducts: [enabledProduct],
      ids: companyIds,
      names: [],
      accountIds: [],
      countryCodes: [],
    };

    return this.apollo
      .use("phoenix")
      .query<any>({
        query: QueryCompanyEnabledProducts,
        variables: {
          list,
          filter,
        },
        fetchPolicy: "network-only",
      })
      .pipe(
        map((response) =>
          response.data.companies.objects.map((company) => ({
            id: company.id,
            name: company.name,
            countryCode: company.countryCode,
            accountName: company.account?.name || "",
            enabled: true,
          })),
        ),
      );
  }
}
